import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTenant } from "../../Contexts/TenantContext";
import { useCart } from "../../Contexts/CartContext";
import { getCartItemKey } from "../../Contexts/CartContext";
import { ProductOrigin } from "../../Enums/ProductOrigin";
import { formatPriceBRL, unformat } from "../../Utils/DataFormatters";
import { toPublicPath } from "../../Utils/publicRoutes";
import { FaWhatsapp } from "react-icons/fa";
import Button from "../Button/Button";

const SIZES_PLUS = ["G1", "G2", "G3"];
const ALL_SIZES = ["P", "M", "G", "GG", ...SIZES_PLUS];

type ProductCardProps = {
  id: string;
  name: string;
  code: string;
  price: number;
  plusPrice?: number | null;
  availableSizes?: string[] | null;
  originalPrice?: number;
  discountPercentage?: number;
  stockQuantity: number;
  isMadeToOrder: boolean;
  origin: ProductOrigin;
  mainImageUrl?: string;
};

const ProductCard = ({
  id,
  name,
  code,
  price,
  plusPrice,
  availableSizes,
  originalPrice,
  discountPercentage,
  stockQuantity,
  isMadeToOrder,
  origin,
  mainImageUrl,
}: ProductCardProps) => {
  const navigate = useNavigate();
  const { tenantPreference, tenantContact } = useTenant();
  const { cartItems, addToCart, removeFromCart } = useCart();

  const enableProductComponents = tenantPreference?.enableProductComponents ?? true;

  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [sizeDropdownOpen, setSizeDropdownOpen] = useState(false);
  const [sizeDropdownRect, setSizeDropdownRect] = useState<{ left: number; width: number; top?: number; bottom?: number } | null>(null);
  const sizeTriggerRef = useRef<HTMLDivElement>(null);
  const sizes = availableSizes?.length ? availableSizes : ALL_SIZES;

  useEffect(() => {
    if (!sizeDropdownOpen) {
      return;
    }
    const close = () => setSizeDropdownOpen(false);
    window.addEventListener("scroll", close, { passive: true, capture: true });
    return () => window.removeEventListener("scroll", close, { capture: true });
  }, [sizeDropdownOpen]);

  const isPlusSize = (sz: string | null) => sz !== null && SIZES_PLUS.includes(sz);
  const resolvePrice = (sz: string | null) =>
    !enableProductComponents && isPlusSize(sz) && plusPrice != null ? plusPrice : price;

  const effectivePrice = resolvePrice(selectedSize);

  const doAddToCart = (sz: string | null) => {
    addToCart({
      id,
      name,
      code,
      price: resolvePrice(sz),
      stockQuantity,
      quantity: 1,
      origin,
      isMadeToOrder,
      image: mainImageUrl,
      size: !enableProductComponents ? (sz ?? undefined) : undefined,
      plusPrice: plusPrice ?? undefined,
    });
  };

  const handleBuy = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!enableProductComponents && !selectedSize) {
      toast.warning("Selecione um tamanho para adicionar ao carrinho.");
      return;
    }
    doAddToCart(selectedSize);
  };

  const hasDiscount = originalPrice && originalPrice > price;
  const isOutOfStock = stockQuantity <= 0 && !isMadeToOrder;

  const isInCart = !enableProductComponents
    ? cartItems.some((i) => i.id === id && i.size === selectedSize)
    : cartItems.some((i) => i.id === id);

  const isSizeInCart = (sz: string) =>
    cartItems.some((i) => i.id === id && i.size === sz);

  const showMadeToOrder = !!isMadeToOrder && stockQuantity <= 0;

  const whatsAppPhone = unformat(tenantContact?.whatsAppNumber || "");
  const hasWhatsApp = whatsAppPhone.length > 0;
  const hideProductCode = tenantPreference?.autoGenerateProductCode === true;

  const showWhatsAppConsultPrice =
    tenantPreference?.whatsAppConsultPriceEnabled === true && effectivePrice === 0 && hasWhatsApp;

  const showWhatsAppConsultStock =
    tenantPreference?.whatsAppConsultStockEnabled === true &&
    !!isMadeToOrder &&
    stockQuantity <= 0 &&
    hasWhatsApp;

  const buildWhatsAppLink = (msgKey: "price" | "stock") => {
    const msg =
      msgKey === "price"
        ? `Olá, gostaria de saber o preço do produto: ${name}`
        : `Olá, gostaria de saber o prazo de entrega do produto: ${name}`;
    return `https://wa.me/${whatsAppPhone}?text=${encodeURIComponent(msg)}`;
  };

  const showSizePicker =
    !enableProductComponents && !isOutOfStock && !showWhatsAppConsultPrice && !showWhatsAppConsultStock;

  const buyButtonLabel =
    showMadeToOrder && !showWhatsAppConsultStock ? "Encomendar" : "Comprar";

  return (
    <div
      className={`group flex sm:block h-full cursor-pointer`}
        role="link"
        tabIndex={0}
        aria-label={name}
        onClick={() => navigate(toPublicPath(`/produtos/${id}`))}
        onKeyDown={(e) => { if (e.key === "Enter") navigate(toPublicPath(`/produtos/${id}`)); }}
      >
        <div
          className={`w-full h-full border transition p-4 text-center bg-white rounded-sm shadow-md border-gray-200 hover:shadow-lg`}
        >
          {mainImageUrl ? (
            <img
              src={mainImageUrl}
              alt={name}
              className="h-28 md:h-40 mx-auto object-contain mb-4"
            />
          ) : (
            <div className="h-28 md:h-40 mx-auto mb-4 flex items-center justify-center bg-gray-100 text-gray-400 rounded">
              <span className="text-sm">{"Sem imagem"}</span>
            </div>
          )}
          <div className="h-12">
            <h3 className="text-base sm:text-md line-clamp-2 font-semibold text-gray-800">
              {name}
            </h3>
          </div>
          {!hideProductCode && (
            <div className="h-2 mt-3">
              <h3 className="text-sm font-medium text-gray-700 line-clamp-2">
                {"COD."} {code}
              </h3>
            </div>
          )}
          <div className={`mt-1 min-h-[44px] flex flex-col justify-center`}>
            {hasDiscount && (
              <div className={`flex items-center gap-2 justify-center`}>
                <span className="text-sm text-gray-500 line-through">
                  {formatPriceBRL(originalPrice!)}
                </span>
                <span className="text-sm text-primary-600 font-medium">
                  -{discountPercentage?.toFixed(0)}%
                </span>
              </div>
            )}
            <p className={`font-bold text-lg sm:text-xl text-primary-600`}>
              {formatPriceBRL(effectivePrice)}
            </p>
          </div>

          {!enableProductComponents && (
            <div
              className="mt-3 mb-1 flex justify-center h-8"
              onClick={(e) => e.stopPropagation()}
            >
          {showSizePicker && (<>
              <div
                ref={sizeTriggerRef}
                className="group/size relative inline-flex items-center rounded border border-gray-300 overflow-hidden cursor-pointer select-none transition-colors hover:border-primary-600"
                onClick={(e) => {
                  e.stopPropagation();
                  if (sizeTriggerRef.current) {
                    const r = sizeTriggerRef.current.getBoundingClientRect();
                    const spaceAbove = r.top;
                    const spaceBelow = window.innerHeight - r.bottom;
                    const openUpward = spaceAbove >= 192 || spaceAbove > spaceBelow;
                    setSizeDropdownRect({
                      left: r.left,
                      width: Math.max(r.width, 90),
                      ...(openUpward ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
                    });
                  }
                  setSizeDropdownOpen((o) => !o);
                }}
              >
                <span className="bg-white text-sm font-medium text-gray-700 pl-3 pr-8 py-1.5 transition-colors group-hover/size:bg-gray-50">
                  {selectedSize ?? "Tam."}
                </span>
                <div className="pointer-events-none absolute right-0 inset-y-0 flex items-center px-1.5 bg-primary-600 transition-colors group-hover/size:bg-primary-700">
                  <svg className="w-3 h-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>

              {sizeDropdownOpen && sizeDropdownRect && createPortal(
                <>
                  <div
                    className="fixed inset-0 z-[9998]"
                    onClick={(e) => { e.stopPropagation(); setSizeDropdownOpen(false); }}
                  />
                  <div
                    className="fixed bg-white border border-gray-200 rounded shadow-lg overflow-y-auto max-h-48 z-[9999]"
                    style={{ left: sizeDropdownRect.left, width: sizeDropdownRect.width, bottom: sizeDropdownRect.bottom }}
                  >
                    <button
                      className="w-full text-left px-3 py-2 text-sm text-gray-400 hover:bg-gray-50 border-b border-gray-100"
                      onClick={(e) => { e.stopPropagation(); setSelectedSize(null); setSizeDropdownOpen(false); }}
                    >
                      {"Tam."}
                    </button>
                    {sizes.map((sz) => (
                      <button
                        key={sz}
                        className={`w-full text-left px-3 py-2 text-sm font-medium ${
                          selectedSize === sz ? "bg-primary-50 text-primary-600" : "text-gray-700 hover:bg-gray-50"
                        }`}
                        onClick={(e) => { e.stopPropagation(); setSelectedSize(sz); setSizeDropdownOpen(false); }}
                      >
                        {sz}{isSizeInCart(sz) ? " ✓" : ""}
                      </button>
                    ))}
                  </div>
                </>,
                document.body
              )}
            </>
          )}
            </div>
          )}

          {showWhatsAppConsultPrice ? (
            <a
              href={buildWhatsAppLink("price")}
              target="_blank"
              rel="noopener noreferrer"
              className={`w-full mt-4 text-sm font-semibold px-4 text-white bg-green-500 hover:bg-green-600 flex items-center justify-center gap-1 rounded py-2`}
              onClick={(e) => e.stopPropagation()}
            >
              <FaWhatsapp />
              {"Consultar preço"}
            </a>
          ) : showWhatsAppConsultStock ? (
            <a
              href={buildWhatsAppLink("stock")}
              target="_blank"
              rel="noopener noreferrer"
              className={`w-full mt-4 text-sm font-semibold px-4 text-white bg-green-500 hover:bg-green-600 flex items-center justify-center gap-1 rounded py-2`}
              onClick={(e) => e.stopPropagation()}
            >
              <FaWhatsapp />
              {"Consultar estoque"}
            </a>
          ) : isOutOfStock ? (
            <Button variant="muted"
              className="w-full mt-4 text-sm px-4 py-2"
              onClick={() => navigate(toPublicPath(`/produtos/${id}`))}
            >
              {"Avise-me"}
            </Button>
          ) : isInCart ? (
            <Button variant="muted"
              className="w-full mt-4 text-sm px-4 py-2"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const key =
                  !enableProductComponents && selectedSize
                    ? getCartItemKey({ id, size: selectedSize })
                    : id;
                removeFromCart(key);
                if (!enableProductComponents) {
                  setSelectedSize(null);
                }
              }}
            >
              {"Remover"}
            </Button>
          ) : (
            <Button
              className="w-full mt-4 text-sm px-4 py-2"
              onClick={handleBuy}
            >
              {buyButtonLabel}
            </Button>
          )}
        </div>
    </div>
  );
};

export default ProductCard;
